import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AirplaneTilt, X, Plus } from "@phosphor-icons/react";

export function DispatchEmailsCard() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/dispatch-emails").then((r) => setCfg(r.data)).catch(() => setCfg({ emails: [], notify_new_request: true, notify_awb: false })); }, []);
  if (!cfg) return null;

  const add = () => {
    const e = draft.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast.error("Enter a valid email address");
    if (cfg.emails.includes(e)) return setDraft("");
    setCfg({ ...cfg, emails: [...cfg.emails, e] }); setDraft("");
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch("/settings/dispatch-emails", cfg); setCfg(data); toast.success("Dispatch team emails saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="hp-card p-6 space-y-5" data-testid="dispatch-emails-card">
      <div className="flex items-start gap-2">
        <AirplaneTilt size={20} weight="fill" className="text-primary mt-0.5 shrink-0" />
        <div>
          <h2 className="font-heading font-semibold text-lg">Dispatch team emails</h2>
          <p className="text-sm text-muted-foreground">Addresses that receive an email when the team sends a new air-cargo booking request (and optionally on AWB / confirmation updates). Dispatch users also see these alerts in-app.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="dispatch-emails-list">
        {cfg.emails.length === 0 && <p className="text-sm text-muted-foreground">No dispatch email configured yet.</p>}
        {cfg.emails.map((e) => (
          <span key={e} className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 pl-3 pr-1.5 py-1 text-sm" data-testid={`dispatch-email-${e}`}>
            {e}
            <button type="button" onClick={() => setCfg({ ...cfg, emails: cfg.emails.filter((x) => x !== e) })} className="h-6 w-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50" aria-label={`Remove ${e}`} data-testid={`dispatch-email-remove-${e}`}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input type="email" placeholder="dispatch@courier.com" className="rounded-sm" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} data-testid="dispatch-email-input" />
        <Button variant="outline" className="rounded-sm shrink-0" onClick={add} data-testid="dispatch-email-add"><Plus size={16} className="mr-1" /> Add</Button>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-normal">Email on new booking request</Label>
          <Switch checked={cfg.notify_new_request} onCheckedChange={(v) => setCfg({ ...cfg, notify_new_request: v })} data-testid="dispatch-email-new-toggle" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-normal">Email on booking confirmed</Label>
          <Switch checked={cfg.notify_awb} onCheckedChange={(v) => setCfg({ ...cfg, notify_awb: v })} data-testid="dispatch-email-awb-toggle" />
        </div>
      </div>
      <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="dispatch-emails-save">{saving ? "Saving…" : "Save dispatch emails"}</Button>
    </div>
  );
}
